import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/brand/Logo";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const STEPS = [
  "Website",
  "Business model",
  "Audience",
  "Brand voice",
  "Growth goals",
  "Integrations",
  "Generate",
];

const VOICES = ["Premium", "Direct", "Concise", "Authoritative", "Playful", "Editorial"];
const GOALS = [
  "more organic traffic",
  "more purchases / orders",
  "more signups / trials",
  "more leads / booked calls",
  "better AI search visibility",
  "higher average order value",
];
const INTEGRATIONS = [
  { id: "shopify", label: "Shopify" },
  { id: "woocommerce", label: "WooCommerce" },
  { id: "stripe", label: "Stripe" },
  { id: "google_search_console", label: "Google Search Console" },
  { id: "google_analytics", label: "Google Analytics (GA4)" },
  { id: "cms", label: "CMS · Webflow / WordPress / Ghost" },
  { id: "openai", label: "OpenAI / LLM provider" },
  { id: "email_marketing", label: "Email marketing" },
  { id: "resend", label: "Resend (transactional email)" },
  { id: "zapier", label: "Zapier / Webhooks" },
];

export default function OnboardingPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [models, setModels] = useState([]);
  const [data, setData] = useState({
    website_url: "https://",
    sitemap_url: "",
    business_model: "ecommerce",
    industry: "",
    audience: "",
    brand_voice: "Premium, direct, confident",
    revenue_model: "one_time",
    sales_channel: "",
    average_order_value: 0,
    growth_goals: ["more organic traffic"],
    integrations: { openai: "emergent_universal_key" },
    publishing_frequency: "3_per_week",
    approval_mode: "review_required",
  });

  useEffect(() => {
    api.get("/meta/business-models").then(({ data }) => setModels(data));
  }, []);

  const toggleGoal = (g) => {
    setData((d) => ({
      ...d,
      growth_goals: d.growth_goals.includes(g) ? d.growth_goals.filter((x) => x !== g) : [...d.growth_goals, g],
    }));
  };

  const toggleIntegration = (id) => {
    setData((d) => {
      const next = { ...d.integrations };
      if (next[id]) delete next[id];
      else next[id] = "placeholder";
      return { ...d, integrations: next };
    });
  };

  const submit = async () => {
    if (!/^https?:\/\//.test(data.website_url) || data.website_url.length < 10) {
      toast.error("Enter a valid website URL");
      setStep(0);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/projects/onboarding", data);
      toast.success("Project ready. Loading your dashboard…");
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Onboarding failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dark-marketing grain min-h-screen flex flex-col" data-testid="onboarding-page">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between">
        <Logo variant="light" />
        <p className="text-xs uppercase tracking-widest text-ivory-200/60">Step {step + 1} of {STEPS.length}</p>
      </header>

      <div className="flex-1 px-6 sm:px-10 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-1 mb-12">
            {STEPS.map((s, i) => (
              <div key={s} className={`h-[3px] flex-1 ${i <= step ? "bg-gold-500" : "bg-ivory-200/15"}`} />
            ))}
          </div>

          <p className="editorial-overline">{STEPS[step]}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ivory-50 mt-4 leading-tight">
            {step === 0 && "Where does your business live?"}
            {step === 1 && "How do you turn attention into revenue?"}
            {step === 2 && "Who are you selling to?"}
            {step === 3 && "How should we sound when we write for you?"}
            {step === 4 && "What does growth mean for you?"}
            {step === 5 && "What should we plug into?"}
            {step === 6 && "Ready to compound."}
          </h1>

          <div className="mt-10">
            {step === 0 && (
              <div className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-ivory-200/60">Website URL</label>
                  <input
                    data-testid="onboarding-website-url"
                    value={data.website_url}
                    onChange={(e) => setData({ ...data, website_url: e.target.value })}
                    placeholder="https://your-brand.com"
                    className="mt-2 w-full bg-transparent border border-ivory-200/20 px-4 py-3 text-ivory-50 rounded-sm focus:border-gold-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ivory-200/60">Sitemap URL (optional)</label>
                  <input
                    data-testid="onboarding-sitemap"
                    value={data.sitemap_url || ""}
                    onChange={(e) => setData({ ...data, sitemap_url: e.target.value })}
                    placeholder="https://your-brand.com/sitemap.xml"
                    className="mt-2 w-full bg-transparent border border-ivory-200/20 px-4 py-3 text-ivory-50 rounded-sm focus:border-gold-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ivory-200/60">Industry (optional)</label>
                  <input
                    data-testid="onboarding-industry"
                    value={data.industry || ""}
                    onChange={(e) => setData({ ...data, industry: e.target.value })}
                    placeholder="e.g. clean beauty, dev tools, financial services"
                    className="mt-2 w-full bg-transparent border border-ivory-200/20 px-4 py-3 text-ivory-50 rounded-sm focus:border-gold-500 outline-none"
                  />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {models.map((b) => (
                  <button
                    key={b.id}
                    data-testid={`onboarding-bm-${b.id}`}
                    onClick={() => setData({ ...data, business_model: b.id })}
                    className={`text-left p-4 rounded-sm border transition-colors ${
                      data.business_model === b.id
                        ? "border-gold-500 bg-white/[0.06] text-ivory-50"
                        : "border-ivory-200/15 text-ivory-200/70 hover:border-ivory-200/30"
                    }`}
                  >
                    <p className="font-serif text-sm">{b.label}</p>
                  </button>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-ivory-200/60">Target audience</label>
                  <textarea
                    data-testid="onboarding-audience"
                    value={data.audience}
                    onChange={(e) => setData({ ...data, audience: e.target.value })}
                    placeholder="e.g. US-based operations leads at 20–200 person B2B SaaS companies who need automated scheduling."
                    rows={4}
                    className="mt-2 w-full bg-transparent border border-ivory-200/20 px-4 py-3 text-ivory-50 rounded-sm focus:border-gold-500 outline-none"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ivory-200/60">Primary sales channel</label>
                    <input
                      data-testid="onboarding-channel"
                      value={data.sales_channel || ""}
                      onChange={(e) => setData({ ...data, sales_channel: e.target.value })}
                      placeholder="Shopify, self-serve web, retail, sales-led"
                      className="mt-2 w-full bg-transparent border border-ivory-200/20 px-4 py-3 text-ivory-50 rounded-sm focus:border-gold-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ivory-200/60">Average order value ($)</label>
                    <input
                      type="number"
                      data-testid="onboarding-aov"
                      value={data.average_order_value || ""}
                      onChange={(e) => setData({ ...data, average_order_value: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      className="mt-2 w-full bg-transparent border border-ivory-200/20 px-4 py-3 text-ivory-50 rounded-sm focus:border-gold-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div className="flex flex-wrap gap-2">
                  {VOICES.map((v) => {
                    const active = data.brand_voice.toLowerCase().includes(v.toLowerCase());
                    return (
                      <button
                        key={v}
                        data-testid={`onboarding-voice-${v}`}
                        onClick={() => {
                          const parts = data.brand_voice.split(",").map((s) => s.trim()).filter(Boolean);
                          const exists = parts.some((p) => p.toLowerCase() === v.toLowerCase());
                          const next = exists ? parts.filter((p) => p.toLowerCase() !== v.toLowerCase()) : [...parts, v];
                          setData({ ...data, brand_voice: next.join(", ") });
                        }}
                        className={`px-4 py-2 text-sm rounded-sm border ${active ? "border-gold-500 text-gold-500" : "border-ivory-200/15 text-ivory-200/70 hover:border-ivory-200/30"}`}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ivory-200/60">Voice description</label>
                  <input
                    data-testid="onboarding-voice-input"
                    value={data.brand_voice}
                    onChange={(e) => setData({ ...data, brand_voice: e.target.value })}
                    className="mt-2 w-full bg-transparent border border-ivory-200/20 px-4 py-3 text-ivory-50 rounded-sm focus:border-gold-500 outline-none"
                  />
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-3">
                {GOALS.map((g) => (
                  <label key={g} className="flex items-center gap-3 p-4 border border-ivory-200/15 rounded-sm hover:border-ivory-200/30 cursor-pointer">
                    <Checkbox
                      checked={data.growth_goals.includes(g)}
                      onCheckedChange={() => toggleGoal(g)}
                      data-testid={`onboarding-goal-${g.replace(/\s+/g, "-").replace(/\//g, "-")}`}
                      className="data-[state=checked]:bg-gold-500 data-[state=checked]:border-gold-500"
                    />
                    <span className="text-ivory-50 text-sm">{g}</span>
                  </label>
                ))}
              </div>
            )}

            {step === 5 && (
              <div className="grid sm:grid-cols-2 gap-3">
                {INTEGRATIONS.map((i) => (
                  <label key={i.id} className="flex items-center gap-3 p-4 border border-ivory-200/15 rounded-sm hover:border-ivory-200/30 cursor-pointer">
                    <Checkbox
                      checked={!!data.integrations[i.id]}
                      onCheckedChange={() => toggleIntegration(i.id)}
                      data-testid={`onboarding-int-${i.id}`}
                      className="data-[state=checked]:bg-gold-500 data-[state=checked]:border-gold-500"
                    />
                    <span className="text-ivory-50 text-sm">{i.label}</span>
                  </label>
                ))}
              </div>
            )}

            {step === 6 && (
              <div className="border border-gold-500/30 p-6 rounded-sm bg-white/[0.02]">
                <p className="editorial-overline">Summary</p>
                <ul className="mt-4 space-y-2 text-sm text-ivory-200/80">
                  <li><span className="text-ivory-200/50">Website:</span> {data.website_url}</li>
                  <li><span className="text-ivory-200/50">Business model:</span> {data.business_model}</li>
                  {data.industry && <li><span className="text-ivory-200/50">Industry:</span> {data.industry}</li>}
                  <li><span className="text-ivory-200/50">Audience:</span> {data.audience || "(not set)"}</li>
                  <li><span className="text-ivory-200/50">Voice:</span> {data.brand_voice}</li>
                  <li><span className="text-ivory-200/50">Goals:</span> {data.growth_goals.join(", ")}</li>
                  <li><span className="text-ivory-200/50">Integrations:</span> {Object.keys(data.integrations).join(", ") || "None"}</li>
                </ul>
              </div>
            )}
          </div>

          <div className="flex justify-between mt-10">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="onboarding-back"
              className="text-sm text-ivory-200/70 hover:text-ivory-50 disabled:opacity-30"
            >
              ← Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                data-testid="onboarding-next"
                className="px-7 py-3 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="onboarding-submit"
                className="px-7 py-3 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm disabled:opacity-50"
              >
                {submitting ? "Generating…" : "Generate growth plan"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
